import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #000;
  color: white;
`;

const HeroSection = styled.section`
  padding: 180px 200px 100px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1200px) {
    padding: 180px 100px 80px;
  }
  
  @media (max-width: 768px) {
    padding: 150px 20px 60px;
  }
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  background: linear-gradient(45deg, rgba(31, 83, 255, 0.1), rgba(255, 67, 163, 0.1));
  z-index: 0;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 60px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const HeroContent = styled.div`
  flex: 1;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  background: rgba(31, 83, 255, 0.1);
  color: #1F53FF;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 30px;
  
  i {
    margin-right: 8px;
  }
`;

const GradientTitle = styled(motion.h1)`
  font-size: 60px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 30px;
  
  .gradient-text {
    background: ${({ theme }) => theme.gradients.text};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const SubTitle = styled(motion.p)`
  font-size: 20px;
  line-height: 1.6;
  margin-bottom: 40px;
  max-width: 600px;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const TrustSignals = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  i {
    margin-right: 8px;
    color: #0df9b6;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: ${({ theme }) => theme.gradients.button};
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    margin-right: 8px;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: white;
  border: 2px solid #1F53FF;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    margin-right: 8px;
  }
  
  &:hover {
    background: rgba(31, 83, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const StatsCard = styled(motion.div)`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 40px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 1024px) {
    max-width: 500px;
    margin: 0 auto;
  }
`;

const StatsTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 30px;
  background: ${({ theme }) => theme.gradients.text};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.text};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const ContentSection = styled.section`
  padding: 100px 200px;
  
  @media (max-width: 1200px) {
    padding: 80px 100px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 48px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 20px;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 60px;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 30px;
  border-left: 4px solid #1F53FF;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(31, 83, 255, 0.1);
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.gradients.button};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  i {
    font-size: 24px;
    color: white;
  }
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 15px;
  background: ${({ theme }) => theme.gradients.text};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
`;

const CardLink = styled.a`
  color: #1F53FF;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMessage = styled(motion.p)`
  font-size: 16px;
  color: #0df9b6;
  margin-top: 20px;
`;

const ErrorMessage = styled(motion.p)`
  font-size: 16px;
  color: #de681d;
  margin-top: 20px;
`;

const SubmitButton = styled(motion.button)`
  background: ${({ theme }) => theme.gradients.button};
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    margin-right: 8px;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const CustomBusinessAppsLandingPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://dvuiiloynbrtdrabtzsg.supabase.co/functions/v1/send-lead-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
        console.error('Submission error:', result);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Network error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <PageContainer>
      <Header />
      
      <HeroSection>
        <FloatingElement
          style={{ top: '10%', right: '-10%', width: '300px', height: '300px' }}
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <FloatingElement
          style={{ bottom: '10%', left: '-5%', width: '200px', height: '200px' }}
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: -3 }}
        />
        
        <ContentWrapper>
          <HeroContent>
            <Badge
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <i className="fas fa-rocket"></i>
              Transform Your Business in 90 Days
            </Badge>
            
            <GradientTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Custom Business Apps That{' '}
              <span className="gradient-text">Flow Differently</span>
            </GradientTitle>
            
            <SubTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Cut manual work by 40% with intelligent ERP, CRM, AI agents, and automation that adapts to your unique business flows. Like lava reshaping landscapes, we transform your operations.
            </SubTitle>
            
            <TrustSignals
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <TrustBadge>
                <i className="fas fa-shield-alt"></i>
                SOC 2 Type II
              </TrustBadge>
              <TrustBadge>
                <i className="fas fa-certificate"></i>
                ISO 27001
              </TrustBadge>
              <TrustBadge>
                <i className="fas fa-users"></i>
                642K+ Users
              </TrustBadge>
            </TrustSignals>
            
            <CTAButtons
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <PrimaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fas fa-calendar-alt"></i>
                Book Solution Design Call
              </PrimaryButton>
              <SecondaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fas fa-calculator"></i>
                Get ROI Estimate
              </SecondaryButton>
            </CTAButtons>
          </HeroContent>
          
          <StatsCard
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <StatsTitle>Real Impact Dashboard</StatsTitle>
            <StatsGrid>
              <StatItem>
                <StatNumber>40%</StatNumber>
                <StatLabel>Manual Work Reduced</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>28%</StatNumber>
                <StatLabel>Faster Order Cycles</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>+15</StatNumber>
                <StatLabel>Forecast Accuracy Pts</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>90</StatNumber>
                <StatLabel>Days to ROI</StatLabel>
              </StatItem>
            </StatsGrid>
          </StatsCard>
        </ContentWrapper>
      </HeroSection>

      <ContentSection style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Why Teams Choose Custom Business Apps
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Every department faces unique challenges. Our solutions flow around obstacles and fill gaps that rigid systems miss.
        </SectionSubtitle>
        
        <Grid>
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <CardIcon>
              <i className="fas fa-cogs"></i>
            </CardIcon>
            <CardTitle>Operations</CardTitle>
            <CardDescription>
              Manual processes slow you down → Intelligent automation speeds you up → 40% time savings
            </CardDescription>
            <CardLink href="#solutions">See ERP Solutions →</CardLink>
          </Card>
          
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <CardIcon>
              <i className="fas fa-chart-line"></i>
            </CardIcon>
            <CardTitle>Sales & RevOps</CardTitle>
            <CardDescription>
              Dirty pipeline data hurts forecasting → Clean CRM with AI insights → 15pt accuracy boost
            </CardDescription>
            <CardLink href="#solutions">See CRM Solutions →</CardLink>
          </Card>
          
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardIcon>
              <i className="fas fa-robot"></i>
            </CardIcon>
            <CardTitle>IT & Innovation</CardTitle>
            <CardDescription>
              Repetitive tasks drain resources → AI agents work 24/7 → Scale without headcount
            </CardDescription>
            <CardLink href="#solutions">See AI Agents →</CardLink>
          </Card>
          
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <CardIcon>
              <i className="fas fa-bullseye"></i>
            </CardIcon>
            <CardTitle>Marketing</CardTitle>
            <CardDescription>
              Poor attribution wastes budget → Multi-channel orchestration → Clear ROI visibility
            </CardDescription>
            <CardLink href="#solutions">See Automation →</CardLink>
          </Card>
          
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardIcon>
              <i className="fas fa-calculator"></i>
            </CardIcon>
            <CardTitle>Finance & Accounting</CardTitle>
            <CardDescription>
              Manual reconciliation creates errors → Automated financial workflows → 95% accuracy improvement
            </CardDescription>
            <CardLink href="#solutions">See Financial Solutions →</CardLink>
          </Card>
          
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <CardIcon>
              <i className="fas fa-user-tie"></i>
            </CardIcon>
            <CardTitle>Human Resources</CardTitle>
            <CardDescription>
              Paper-based processes slow hiring → Digital HR workflows → 60% faster onboarding
            </CardDescription>
            <CardLink href="#solutions">See HR Solutions →</CardLink>
          </Card>
        </Grid>
      </ContentSection>

      {/* Solutions Section */}
      <ContentSection id="solutions">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Solutions That Adapt to You
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Our organic approach molds perfectly to your business vision, creating custom applications that grow with you.
        </SectionSubtitle>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '40px', marginTop: '60px' }}>
          {/* ERP Solution */}
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ padding: '40px' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '30px' }}>
              <div style={{ width: '64px', height: '64px', background: 'linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '20px', flexShrink: 0 }}>
                <i className="fas fa-industry" style={{ fontSize: '28px', color: 'white' }}></i>
              </div>
              <div>
                <CardTitle>Intelligent ERP Systems</CardTitle>
                <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.7)' }}>Close faster, plan smarter, and see your supply chain in real time</p>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'rgba(31, 83, 255, 0.1)', padding: '20px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1F53FF' }}>Outcomes</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'rgba(31, 83, 255, 0.8)' }}>
                  <li>• 28% faster cycles</li>
                  <li>• Real-time visibility</li>
                  <li>• Exception handling</li>
                </ul>
              </div>
              <div style={{ background: 'rgba(13, 249, 182, 0.1)', padding: '20px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#0df9b6' }}>Features</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'rgba(13, 249, 182, 0.8)' }}>
                  <li>• AI forecasting</li>
                  <li>• AP automation</li>
                  <li>• Supply chain AI</li>
                </ul>
              </div>
              <div style={{ background: 'rgba(222, 104, 29, 0.1)', padding: '20px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#de681d' }}>Integrations</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'rgba(222, 104, 29, 0.8)' }}>
                  <li>• NetSuite</li>
                  <li>• SAP</li>
                  <li>• QuickBooks</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* CRM Solution */}
          <Card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ padding: '40px' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '30px' }}>
              <div style={{ width: '64px', height: '64px', background: 'linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '20px', flexShrink: 0 }}>
                <i className="fas fa-users" style={{ fontSize: '28px', color: 'white' }}></i>
              </div>
              <div>
                <CardTitle>Revenue Intelligence CRM</CardTitle>
                <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.7)' }}>Clean pipeline, smarter prioritization, and AI-assisted outreach</p>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'rgba(31, 83, 255, 0.1)', padding: '20px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1F53FF' }}>Outcomes</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'rgba(31, 83, 255, 0.8)' }}>
                  <li>• +15pt accuracy</li>
                  <li>• Clean pipeline</li>
                  <li>• Faster deals</li>
                </ul>
              </div>
              <div style={{ background: 'rgba(13, 249, 182, 0.1)', padding: '20px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#0df9b6' }}>Features</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'rgba(13, 249, 182, 0.8)' }}>
                  <li>• Rev intelligence</li>
                  <li>• Call summaries</li>
                  <li>• Next best action</li>
                </ul>
              </div>
              <div style={{ background: 'rgba(222, 104, 29, 0.1)', padding: '20px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#de681d' }}>Integrations</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'rgba(222, 104, 29, 0.8)' }}>
                  <li>• Salesforce</li>
                  <li>• HubSpot</li>
                  <li>• Pipedrive</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </ContentSection>

      {/* Final CTA Section */}
      <ContentSection style={{ background: 'linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <FloatingElement
          style={{ top: '20%', left: '10%', width: '200px', height: '200px', background: 'rgba(255,255,255,0.1)' }}
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <FloatingElement
          style={{ bottom: '20%', right: '10%', width: '300px', height: '300px', background: 'rgba(255,255,255,0.1)' }}
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: -4 }}
        />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <motion.h2
            style={{ fontSize: '48px', fontWeight: '700', color: 'white', marginBottom: '20px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Ready to Break Free from Rigid Systems?
          </motion.h2>
          <motion.p
            style={{ fontSize: '20px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '40px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Join 642K+ users who've transformed their operations with business apps that flow differently. Let's reshape your digital landscape together.
          </motion.p>
          
          <motion.div
            style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '60px', flexWrap: 'wrap' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.button
              style={{ background: 'white', color: '#1F53FF', border: 'none', padding: '16px 40px', borderRadius: '8px', fontSize: '18px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              whileHover={{ scale: 1.05, background: 'rgba(255, 255, 255, 0.9)' }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fas fa-calendar-alt" style={{ marginRight: '12px' }}></i>
              Book Your Solution Design Call
            </motion.button>
            <motion.button
              style={{ background: 'transparent', color: 'white', border: '2px solid white', padding: '14px 40px', borderRadius: '8px', fontSize: '18px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              whileHover={{ scale: 1.05, background: 'rgba(255, 255, 255, 0.1)' }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fas fa-play" style={{ marginRight: '12px' }}></i>
              Watch 3-Minute Demo
            </motion.button>
          </motion.div>
          
          {/* Lead Capture Form */}
          <motion.div
            style={{ background: 'white', borderRadius: '16px', padding: '40px', maxWidth: '600px', margin: '0 auto' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1F53FF', marginBottom: '30px' }}>Get Your Free ROI Assessment</h3>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleInputChange}
                style={{ padding: '16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', color: '#333' }}
                required
              />
              <input
                type="text"
                name="company"
                placeholder="Company Name"
                value={formData.company}
                onChange={handleInputChange}
                style={{ padding: '16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', color: '#333' }}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Work Email"
                value={formData.email}
                onChange={handleInputChange}
                style={{ padding: '16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', color: '#333', gridColumn: '1 / -1' }}
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                style={{ padding: '16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', color: '#333' }}
              />
              <textarea
                name="message"
                placeholder="Tell us about your business needs..."
                value={formData.message}
                onChange={handleInputChange}
                style={{ 
                  padding: '16px', 
                  border: '1px solid #ddd', 
                  borderRadius: '8px', 
                  fontSize: '16px', 
                  color: '#333', 
                  gridColumn: '1 / -1',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
                rows={3}
              />
              <motion.button
                type="submit"
                style={{ background: 'linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%)', color: 'white', border: 'none', padding: '16px', borderRadius: '8px', fontSize: '18px', fontWeight: '700', cursor: 'pointer', gridColumn: '1 / -1' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get My Free ROI Assessment
              </motion.button>
            </form>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '20px', textAlign: 'center' }}>
              <i className="fas fa-lock" style={{ marginRight: '4px' }}></i>
              Your information is protected by SOC 2 Type II security standards
            </p>
          </motion.div>
        </div>
      </ContentSection>

      <Footer />
    </PageContainer>
  );
};

export default CustomBusinessAppsLandingPage;
